import React, { useState } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import {
  Alert, Box, Button, CircularProgress, Dialog, IconButton, Tooltip, Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import LaptopMacRoundedIcon from '@mui/icons-material/LaptopMacRounded'
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import { replaceSessionToken, timeAgo } from '../../utils/authToken'

// Sign-ins to an admin, vendor or agent account and "sign out of all devices"
// (backend migration 137). Login tokens last 30 days; signing out everywhere
// ends every one of them at once.

const VENDOR_LOGINS = gql`
  query VendorLogins($vendorId: ID!, $limit: Int) {
    vendorLogins(vendorId: $vendorId, limit: $limit) { _id at ip device userAgent }
  }
`
export const SIGN_OUT_EVERYWHERE = gql`
  mutation SignOutVendorEverywhere($vendorId: ID!) {
    signOutVendorEverywhere(vendorId: $vendorId) { ok token }
  }
`

const isPhone = device => /iPhone|Android|iPad/.test(device || '')

/** The list itself — also used inline on a profile page. */
export function SignInsList({ vendorId, self = false, onSignedOut }) {
  const { data, loading, error, refetch } = useQuery(VENDOR_LOGINS, {
    variables: { vendorId: String(vendorId), limit: 20 },
    skip: !vendorId,
    fetchPolicy: 'network-only'
  })
  const [confirming, setConfirming] = useState(false)
  const [done, setDone] = useState('')
  const [signOut, { loading: signingOut, error: signOutError }] = useMutation(SIGN_OUT_EVERYWHERE, {
    onCompleted: res => {
      // Signed out yourself: this device carries on with the fresh token.
      replaceSessionToken(res?.signOutVendorEverywhere?.token)
      setConfirming(false)
      setDone(self
        ? 'Every other device is signed out. This one stays signed in.'
        : 'Every device signed in to this account is now signed out.')
      refetch()
      if (onSignedOut) onSignedOut()
    }
  })
  const rows = data?.vendorLogins ?? []

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      {signOutError && <Alert severity="error" sx={{ mb: 2 }}>{signOutError.message}</Alert>}
      {done && <Alert severity="success" sx={{ mb: 2 }}>{done}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={26} /></Box>
      ) : rows.length === 0 ? (
        <Typography sx={{ fontSize: '0.875rem', color: '#64748b', py: 2 }}>
          No sign-ins recorded yet. Sign-ins are recorded from now on.
        </Typography>
      ) : (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
          {rows.map((r, i) => (
            <Box key={r._id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#f1f5f9', color: '#475569', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                {isPhone(r.device) ? <PhoneIphoneRoundedIcon sx={{ fontSize: 20 }} /> : <LaptopMacRoundedIcon sx={{ fontSize: 20 }} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Tooltip title={r.userAgent || ''} placement="top-start">
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>{r.device || 'Unknown device'}</Typography>
                </Tooltip>
                <Typography noWrap sx={{ fontSize: '0.78rem', color: '#64748b' }}>{r.ip || 'Address not recorded'}</Typography>
              </Box>
              <Tooltip title={r.at ? new Date(r.at).toLocaleString() : ''}>
                <Typography sx={{ fontSize: '0.78rem', color: '#64748b', whiteSpace: 'nowrap' }}>{timeAgo(r.at)}</Typography>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 2, p: 2, borderRadius: '14px', bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#991b1b' }}>
          {self ? 'Sign out of all other devices' : 'Sign out of all devices'}
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: '#7f1d1d', mt: 0.5, mb: 1.5 }}>
          {self
            ? 'Ends every other session of your account, wherever it is signed in. You stay signed in here.'
            : 'Ends every session of this account, wherever it is signed in. They will need to sign in again.'}
        </Typography>
        {confirming ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              disableElevation
              disabled={signingOut}
              onClick={() => signOut({ variables: { vendorId: String(vendorId) } })}
              startIcon={signingOut ? <CircularProgress size={16} color="inherit" /> : <LogoutRoundedIcon />}
              sx={{ bgcolor: '#dc2626', textTransform: 'none', fontWeight: 700, borderRadius: '10px', '&:hover': { bgcolor: '#b91c1c' } }}
            >
              Yes, sign out everywhere
            </Button>
            <Button onClick={() => setConfirming(false)} sx={{ textTransform: 'none', color: '#475569', fontWeight: 600 }}>Cancel</Button>
          </Box>
        ) : (
          <Button
            variant="outlined"
            onClick={() => { setDone(''); setConfirming(true) }}
            startIcon={<LogoutRoundedIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', color: '#dc2626', borderColor: '#fca5a5', '&:hover': { borderColor: '#dc2626', bgcolor: '#fff' } }}
          >
            {self ? 'Sign out other devices' : 'Sign out of all devices'}
          </Button>
        )}
      </Box>
    </Box>
  )
}

/** The list in a dialog, for an account picked from a table. */
export default function SignInsDialog({ open, onClose, account, self = false, onSignedOut }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '20px' } }}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#0f172a' }}>Sign-ins</Typography>
            <Typography noWrap sx={{ fontSize: '0.85rem', color: '#64748b' }}>
              {account?.name || account?.email}{account?.name && account?.email ? ` · ${account.email}` : ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close" size="small"><CloseIcon /></IconButton>
        </Box>
        {open && account?._id && <SignInsList vendorId={account._id} self={self} onSignedOut={onSignedOut} />}
      </Box>
    </Dialog>
  )
}
